import { jsx as _jsx } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import { useMemo } from "react";
import classnames from "classnames";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Autocomplete, MenuItem, TextField } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import { FontAwesomeIcon } from "./FontAwesomeIcon";
import { faAngleDown } from "@fortawesome/free-solid-svg-icons";
import "./DoraSelect.scss";
import { sortByStartsWith } from "../../helpers/sort-by-starts-with";
import { useElementId } from "./useElementId";
const setInputWidth = (text, hasIcon) => {
    return 65 + text.length * 9 + (hasIcon ? 20 : 0);
};
const DoraSelect = (props) => {
    const { autofocus, autoselect, id, icon, options, placeholder, fullWidth, className, freeSolo, disabled, controlField, onInputChange, onChange, error, value, customRenderOption, customGetOptionLabel, renderOptionsInBatchesNumber, label, loading, } = props;
    const { t } = useTranslation("components");
    const [inputValue, setInputValue] = useState("");
    const [page, setPage] = useState(1);
    const [length, setLength] = useState(placeholder ? setInputWidth(placeholder) : 120);
    const wrapperClasses = classnames("dora-select__wrapper", disabled ? "dora-select__wrapper--disabled" : null, error ? "dora-select__wrapper--error" : null, className);
    const staticId = useElementId();
    const customId = id || staticId;
    const onOptionSelected = (v) => {
        if (onChange) {
            onChange(v?.value);
        }
        setLength(setInputWidth(v?.label || "", !!icon));
    };
    const onAdornmentClick = () => {
        const input = document.getElementById(customId);
        input?.parentElement?.click();
    };
    const valueProp = controlField
        ? {
            value: options.find((option) => option.value === controlField.value) || null,
        }
        : {
            inputValue: options.find((option) => option.value === value)?.label || value,
        };
    const defaultGetOptionLabel = (option) => option?.label || "";
    const textFieldId = useElementId();
    const defaultRenderOption = (props, option) => {
        return (_createElement(MenuItem, { ...props, key: option.value, disabled: option.unselectable }, option.label));
    };
    const handleInputChange = (e, newInputValue) => {
        setInputValue(newInputValue);
        setPage(1);
    };
    const onMenuScroll = (e) => {
        if (!renderOptionsInBatchesNumber) {
            return;
        }
        const listbox = e.target;
        if (listbox.scrollTop + listbox.clientHeight === listbox.scrollHeight) {
            setPage((prevPage) => prevPage + 1);
        }
    };
    const declarativeFilteredOptions = useMemo(() => {
        const filterText = inputValue.toLowerCase();
        if (inputValue) {
            return options
                .filter((o) => o.label.toLowerCase().includes(filterText) ||
                o.labelSecondary?.toLowerCase().includes(filterText) ||
                o.address?.toLowerCase().includes(filterText))
                .sort(sortByStartsWith(filterText, "label", "labelSecondary"));
        }
        return options;
    }, [inputValue, options]);
    const declarativeRenderedOptions = useMemo(() => renderOptionsInBatchesNumber
        ? declarativeFilteredOptions.slice(0, page * renderOptionsInBatchesNumber)
        : declarativeFilteredOptions, [declarativeFilteredOptions, page, renderOptionsInBatchesNumber]);
    const actualOptions = useMemo(() => {
        if (loading) {
            return [
                {
                    value: "",
                    label: t("optionLabels.loading"),
                    unselectable: true,
                },
            ];
        }
        return declarativeRenderedOptions.length
            ? declarativeRenderedOptions
            : [
                {
                    value: "",
                    label: t("optionLabels.noOptions"),
                    unselectable: true,
                },
            ];
    }, [declarativeRenderedOptions, t, loading]);
    return (_jsx(FormControl, { size: "small", className: wrapperClasses, style: { ...props.style }, children: _jsx(Autocomplete, { fullWidth: true, size: "small", autoSelect: autoselect, sx: {
                margin: 0,
                background: disabled ? "var(--gray-100)" : "white",
                "& input::placeholder": {
                    color: "var(--gray-800)",
                    fontWeight: 400,
                },
                "& fieldset": { borderColor: "var(--gray-300)" },
                "& .MuiOutlinedInput-root:not(.Mui-focused):hover fieldset": {
                    borderColor: "var(--gray-400) !important",
                },
                ...(!disabled && { "&:hover": { borderColor: "var(--gray-300)" } }),
                "& .MuiFormControl-root.MuiFormControl-marginDense.MuiFormControl-fullWidth.MuiTextField-root": {
                    margin: "0 !important",
                    ...(fullWidth && { width: "100% !important" }),
                },
                "& .MuiFormControl-root": fullWidth
                    ? { width: "100% !important" }
                    : {},
                "& .MuiAutocomplete-endAdornment": {
                    right: "0.25rem !important",
                },
            }, options: actualOptions, slotProps: {
                listbox: { onScroll: onMenuScroll },
                popper: { sx: { minWidth: "fit-content" } },
                clearIndicator: { sx: { color: "var(--gray-400)" } },
                popupIndicator: {
                    sx: {
                        color: "var(--gray-400)",
                        fontSize: "1rem",
                        padding: "0.5rem",
                    },
                },
            }, popupIcon: _jsx(FontAwesomeIcon, { icon: faAngleDown }), filterOptions: (vals, { inputValue }) => {
                return vals.filter((v) => v.label.toLowerCase().includes(inputValue.toLowerCase()) ||
                    v.labelSecondary
                        ?.toLowerCase()
                        .includes(inputValue.toLowerCase()) ||
                    v.address?.toLowerCase().includes(inputValue.toLowerCase()));
            }, getOptionLabel: customGetOptionLabel || defaultGetOptionLabel, renderOption: customRenderOption || defaultRenderOption, renderInput: (params) => (_jsx(TextField, { ...params, id: textFieldId, placeholder: placeholder, label: label, autoFocus: autofocus, sx: fullWidth
                    ? {}
                    : {
                        width: `${length}px !important`,
                        minWidth: `${length}px !important`,
                    }, InputProps: {
                    ...params.InputProps,
                    sx: { "& .MuiInputBase-input": { textIndent: icon ? 20 : 0 } },
                    startAdornment: icon ? (_jsx("div", { className: "dora-select__icon", onClick: onAdornmentClick, children: _jsx(FontAwesomeIcon, { icon: icon }) })) : null,
                } })), disabled: disabled, id: customId, ...(freeSolo && { freeSolo }), ...controlField, ...valueProp, ...(onInputChange && { onInputChange }), inputValue: inputValue, onInputChange: handleInputChange, onChange: controlField
                ? (_, v) => {
                    controlField.onChange(v?.value || null);
                    onOptionSelected(v);
                }
                : (_, v) => onOptionSelected(v) }) }));
};
export { DoraSelect as DoraSelectForClients };
//# sourceMappingURL=DoraSelectForClients.js.map